/**
 * TerminatePolicyModal — confirmation modal for the three terminate
 * endpoints on /account-insurance/{id}/{redeem|lapse|mature} (#322,
 * 2026-06-08).
 *
 * Termination is a TERMINAL Active → Closed transition. Unlike Detach
 * (#275, reversible via Attach), Closed is one-way. Active Indirect
 * Accounts cascade to Closed (also terminal). Already-Inactive Indirect
 * Accounts stay Inactive (no override — they were already in a tolerant
 * limbo and shouldn't be forced terminal).
 *
 * The modal is parametrised over the three reasons so the same friction
 * model — preview cascade + explicit confirm + stronger red/orange
 * accent vs Detach's yellow — applies uniformly. Charter §5
 * (human-in-the-loop) — irreversible cross-row cascade demands a
 * deliberate yes.
 */
import {
  Alert, Badge, Button, Group, Modal, Stack, Text, Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconCircleX } from '@tabler/icons-react';

import { ApiError } from '../api/client';
import type { AccountInsurance, UserCloseReason } from '../api/types';
import {
  useLapsePolicy, useMaturePolicy, useRedeemPolicy,
} from '../hooks/useAccountInsurance';
import { useAlertModal } from './AlertModalProvider';

/** Display metadata per reason — surfaced in modal title, alert body,
 *  and the action button. Single source of truth so adding a new reason
 *  is a one-place change. */
// #29 (2026-06-11): keyed by UserCloseReason, NOT CloseReason —
// AGREEMENT_CLOSED is system-stamped by the Agreement→Closed cascade
// and must never be offerable here.
const REASON_META: Record<UserCloseReason, {
  verb: string;
  pastTense: string;
  description: string;
}> = {
  REDEEMED: {
    verb: 'Redeem',
    pastTense: 'Redeemed',
    description: 'policyholder surrendered the policy for cash value',
  },
  LAPSED: {
    verb: 'Lapse',
    pastTense: 'Lapsed',
    description: 'policy terminated due to non-payment of premiums',
  },
  MATURED: {
    verb: 'Mature',
    pastTense: 'Matured',
    description: 'policy reached the end of its term and paid out',
  },
};

interface Props {
  opened: boolean;
  onClose: () => void;
  policy: AccountInsurance;
  reason: UserCloseReason;
  /** Optional success handler — used by detail pages to navigate or
   *  refresh local UI after the termination lands. */
  onSuccess?: () => void;
  /** Phase 1.5 (#375, 2026-06-09): when provided, the modal becomes a
   *  STAGING gesture instead of an immediate commit. Confirm reads
   *  "Stage {verb}" and calls onStage(reason); the parent's Save handler
   *  commits via the appropriate terminate hook later. */
  onStage?: (reason: UserCloseReason) => void;
}

export function TerminatePolicyModal({
  opened, onClose, policy, reason, onSuccess, onStage,
}: Props) {
  const redeemMut = useRedeemPolicy();
  const lapseMut  = useLapsePolicy();
  const matureMut = useMaturePolicy();
  const { showAlert } = useAlertModal();

  const meta = REASON_META[reason];
  const mut =
    reason === 'REDEEMED' ? redeemMut :
    reason === 'LAPSED'   ? lapseMut  :
                            matureMut;

  const indirectCount = policy.account_ids.length;
  const isStaging = !!onStage;

  function handleTerminate() {
    if (isStaging) {
      onStage!(reason);
      onClose();
      return;
    }
    mut.mutate(policy.id, {
      // #26 (2026-06-10): terminate is maker-checker now — surface the RM
      // pending case on this legacy immediate path too.
      onSuccess: (r) => {
        if (r.pending) {
          notifications.show({
            color: 'orange',
            message: 'Termination filed for approval — the policy stays Active until Compliance approves.',
          });
        }
        onClose();
        onSuccess?.();
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          showAlert({
            color: 'red',
            title: `Cannot ${meta.verb.toLowerCase()} Policy`,
            message: err.detail,
          });
        }
      },
    });
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      title={
        <Group gap="xs">
          <IconCircleX size={20} />
          <Title order={4}>{meta.verb} Policy</Title>
        </Group>
      }
    >
      <Stack>
        <Alert color="red" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">
            {meta.verb} moves <strong>{policy.insurer} — {policy.policy_number}</strong>{' '}
            to <Badge color="red" variant="light">Closed</Badge> with reason{' '}
            <Badge color="red" variant="light">{meta.pastTense}</Badge>{' '}
            ({meta.description}).
          </Text>
          <Text size="xs" c="red.7" mt={6}>
            <strong>This is irreversible.</strong> Closed Policies cannot be
            re-attached or restored. Use Detach instead if you need a
            reversible move to limbo.
          </Text>
        </Alert>

        {indirectCount > 0 && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">
              <strong>{indirectCount}</strong> Indirect Account{indirectCount === 1 ? '' : 's'}{' '}
              currently Active and owned by this Policy will cascade to{' '}
              <Badge color="red" variant="light">Closed</Badge>.
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              Already-Inactive Indirect Accounts stay Inactive. Closed
              Accounts also cannot be reversed.
            </Text>
          </Alert>
        )}

        {indirectCount === 0 && (
          <Text size="sm" c="dimmed">
            No Indirect Accounts are owned by this Policy — only the Policy
            itself will change state.
          </Text>
        )}

        <Group justify="space-between" pt="sm">
          <Button variant="default" onClick={onClose}
            disabled={mut.isPending}>
            Cancel
          </Button>
          <Button color="red"
            leftSection={<IconCircleX size={14} />}
            onClick={handleTerminate}
            loading={!isStaging && mut.isPending}>
            {isStaging ? `Stage ${meta.verb}` : `${meta.verb} Policy`}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
